/**
 * Mounts one data source for a route tree: `/` → live, `/test` → mock.
 *
 * Loads the source's bootstrap (clock, filter options, workspace, session)
 * before rendering anything below it, seeds the filters of the page's filter
 * scope (features/filters/scope.ts) — from what was saved for it, the first
 * time a live scope is entered — and provides the router-aware link and the
 * source clock to `@deepdive-ui`.
 */
import { Button, PageState, UIProvider } from '@deepdive-ui';
import { useEffect, useMemo } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import { Navigate, Outlet } from 'react-router';

import { RouterLink } from '@/app/router/RouterLink';
import type { SourceId } from '@/data-sources/contract';
import { AppliedFiltersContext } from '@/features/data/AppliedFiltersContext';
import { bootstrapApi, sourcedArg } from '@/features/data/bootstrap.api';
import { DataSourceContext, type DataSourceContextValue } from '@/features/data/DataSourceContext';
import { executiveFilters, useLastDayWithData } from '@/features/executive/defaults';
import { filterStateApi } from '@/features/filters/filterState.api';
import { startFilterPersistence } from '@/features/filters/persistence';
import { filterScopeFor } from '@/features/filters/scope';
import { useLeafRoute } from '@/features/filters/useReportScope';
import { fromIsoDate } from '@/lib/analytics/dates';
import { useDebouncedValue } from '@/lib/hooks/useDebouncedValue';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { initFilters } from '@/store/slices/filtersSlice';

const FILTER_DEBOUNCE_MS = 300;

startFilterPersistence();

/** Equal when it is the same scope's same filter state — a fresh wrapper object is not a change. */
const sameScopedFilters = (
  a: { key: string; filters: unknown },
  b: { key: string; filters: unknown },
) => a.key === b.key && a.filters === b.filters;

export function DataSourceLayout({ source, basePath }: { source: SourceId; basePath: string }) {
  const dispatch = useAppDispatch();
  const {
    data: bootstrap,
    error,
    isError,
    refetch,
  } = bootstrapApi.useBootstrapQuery(sourcedArg(source));
  // The page being shown — read from the leaf match: this layout sits above it.
  const { routeId, params } = useLeafRoute();
  const scope = filterScopeFor(source, routeId, params);
  const filters = useAppSelector((state) => state.filters[scope.key]);

  // A scope's saved filters, read once — only while it has none in the store.
  const saved = filterStateApi.useSavedFiltersQuery(
    bootstrap && scope.ref && !filters ? { source, params: scope.ref } : skipToken,
  );
  const savedSettled = !scope.ref || saved.isSuccess || saved.isError;
  // The executive dashboard opens on this month up to its last day with data.
  const lastDay = useLastDayWithData(
    source,
    bootstrap?.today,
    Boolean(bootstrap) && scope.defaults === 'executive' && !filters,
  );

  // The first filters of a scope apply at once; only later bursts are debounced.
  const debounced = useDebouncedValue(
    { key: scope.key, filters },
    FILTER_DEBOUNCE_MS,
    sameScopedFilters,
  );
  const applied = debounced.key === scope.key ? (debounced.filters ?? filters) : filters;

  useEffect(() => {
    if (!bootstrap || filters || !savedSettled || !lastDay.settled) return;
    // A saved state that could not be read falls back to the defaults.
    const seed =
      scope.defaults === 'executive'
        ? executiveFilters(bootstrap.today, bootstrap.workspace.brand, lastDay.day)
        : (saved.data ?? null);
    dispatch(initFilters({ scope: scope.key, today: bootstrap.today, saved: seed }));
  }, [
    bootstrap,
    dispatch,
    filters,
    savedSettled,
    saved.data,
    scope.key,
    scope.defaults,
    lastDay.settled,
    lastDay.day,
  ]);

  const context = useMemo<DataSourceContextValue | null>(
    () => (bootstrap ? { source, basePath, bootstrap, filterScope: scope.key } : null),
    [bootstrap, source, basePath, scope.key],
  );
  const today = useMemo(() => (bootstrap ? fromIsoDate(bootstrap.today) : undefined), [bootstrap]);

  if (isError) {
    // The refresh failed and the base query already cleared the session.
    if (source === 'live' && error && 'kind' in error && error.kind === 'unauthorized') {
      return <Navigate to="/login" replace />;
    }
    const notConnected = error && 'kind' in error && error.kind === 'notConnected';
    return (
      <PageState
        fullScreen
        role="alert"
        title={
          notConnected
            ? 'The live dashboard is not connected yet'
            : 'We couldn’t load your workspace'
        }
        description={
          notConnected
            ? 'This environment has no API behind the dashboard. The full dashboard with sample data is available at /test where enabled.'
            : 'Check your connection and try again.'
        }
        actions={
          !notConnected && (
            <Button hierarchy="primary" onClick={() => void refetch()}>
              Try again
            </Button>
          )
        }
      />
    );
  }

  // Nothing renders until the source is ready and its filters are seeded.
  if (!context || !filters || !applied) return null;

  return (
    <DataSourceContext.Provider value={context}>
      <AppliedFiltersContext.Provider value={applied}>
        <UIProvider linkComponent={RouterLink} today={today}>
          <Outlet />
        </UIProvider>
      </AppliedFiltersContext.Provider>
    </DataSourceContext.Provider>
  );
}
